"use client";

import { c as _c } from "react/compiler-runtime";
import type { CommunityMembersQuery } from "@/app/network/page";
import { SortDirection } from "@/entities/ISort";
import type { ApiCommunityMemberOverview } from "@/generated/models/ApiCommunityMemberOverview";
import type { Page } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import CommunityMembersMobileSortContent from "./members-table/CommunityMembersMobileSortContent";
import CommunityMembersTable from "./members-table/CommunityMembersTable";
import CommunityMembersTableSkeleton from "./members-table/CommunityMembersTableSkeleton";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import CommonTablePagination from "@/components/utils/table/paginator/CommonTablePagination";
import { useSetTitle } from "@/contexts/TitleContext";
import { ApiCommunityMembersSortOption } from "@/generated/models/ApiCommunityMembersSortOption";
import { selectActiveGroupId } from "@/store/groupSlice";
import { faArrowDownWideShort } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import GroupsSidebar from "../groups/sidebar/GroupsSidebar";
import MobileWrapperDialog from "../mobile-wrapper-dialog/MobileWrapperDialog";
interface QueryUpdateInput {
  name: keyof typeof SEARCH_PARAMS_FIELDS;
  value: string | null;
}
const SEARCH_PARAMS_FIELDS = {
  page: "page",
  sortBy: "sort-by",
  sortDirection: "sort-direction",
  group: "group"
} as const;
export default function CommunityMembers() {
  const $ = _c(116);
  useSetTitle("Network");
  const defaultSortBy = ApiCommunityMembersSortOption.Level;
  const defaultSortDirection = SortDirection.DESC;
  const defaultPageSize = 50;
  const defaultPage = 1;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeGroupId = useSelector(selectActiveGroupId);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = sort => {
      if (!sort) {
        return defaultSortBy;
      }
      if (Object.values(ApiCommunityMembersSortOption).includes(sort.toLowerCase() as ApiCommunityMembersSortOption)) {
        return sort.toLowerCase() as ApiCommunityMembersSortOption;
      }
      return defaultSortBy;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const convertSortBy = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = sortDirection => {
      if (!sortDirection) {
        return defaultSortDirection;
      }
      if (Object.values(SortDirection).includes(sortDirection.toUpperCase() as SortDirection)) {
        return sortDirection.toUpperCase() as SortDirection;
      }
      return defaultSortDirection;
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const convertSortDirection = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams?.get(SEARCH_PARAMS_FIELDS.page) || "";
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const page = parseInt(t2);
  let group;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[4] !== page || $[5] !== searchParams) {
    const sortBy = searchParams?.get(SEARCH_PARAMS_FIELDS.sortBy);
    const sortDirection_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.sortDirection);
    let t7;
    if ($[11] !== searchParams) {
      t7 = searchParams?.get(SEARCH_PARAMS_FIELDS.group);
      $[11] = searchParams;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    group = t7;
    t3 = page || 1;
    t4 = 50;
    t5 = sortBy ? convertSortBy(sortBy) : defaultSortBy;
    t6 = sortDirection_0 ? convertSortDirection(sortDirection_0) : defaultSortDirection;
    $[4] = page;
    $[5] = searchParams;
    $[6] = group;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    group = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let query;
  if ($[13] !== group || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    query = {
      page: t3,
      page_size: t4,
      sort: t5,
      sort_direction: t6
    };
    if (group) {
      query.group_id = group;
    }
    $[13] = group;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = query;
  } else {
    query = $[18];
  }
  const params = query;
  let t7;
  if ($[19] !== searchParams) {
    t7 = (updateItems, t8) => {
      const lowerCase = t8 === undefined ? true : t8;
      const searchParamsStr = new URLSearchParams(searchParams?.toString());
      for (const {
        name,
        value
      } of updateItems) {
        const key = SEARCH_PARAMS_FIELDS[name];
        if (!value) {
          searchParamsStr.delete(key);
        } else {
          searchParamsStr.set(key, lowerCase ? value.toLowerCase() : value);
        }
      }
      return searchParamsStr.toString();
    };
    $[19] = searchParams;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const createQueryString = t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = t9 => {
      const {
        newSortBy,
        currentSortBy,
        currentSortDirection
      } = t9;
      if (newSortBy === currentSortBy) {
        if (currentSortDirection === SortDirection.ASC) {
          return SortDirection.DESC;
        }
        return SortDirection.ASC;
      }
      return defaultSortDirection;
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const calculateSortDirection = t8;
  let t9;
  if ($[22] !== params) {
    t9 = () => params;
    $[22] = params;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const [debouncedParams, setDebouncedParams] = useState(t9);
  let t10;
  if ($[24] !== params) {
    t10 = () => setDebouncedParams(params);
    $[24] = params;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== params) {
    t11 = [params];
    $[26] = params;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useDebounce(t10, 200, t11);
  let t12;
  if ($[28] !== debouncedParams.group_id || $[29] !== debouncedParams.page || $[30] !== debouncedParams.page_size || $[31] !== debouncedParams.sort || $[32] !== debouncedParams.sort_direction) {
    t12 = [QueryKey.COMMUNITY_MEMBERS_TOP, {
      page: debouncedParams.page,
      pageSize: debouncedParams.page_size,
      sort: debouncedParams.sort,
      sortDirection: debouncedParams.sort_direction,
      groupId: debouncedParams.group_id
    }];
    $[28] = debouncedParams.group_id;
    $[29] = debouncedParams.page;
    $[30] = debouncedParams.page_size;
    $[31] = debouncedParams.sort;
    $[32] = debouncedParams.sort_direction;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== debouncedParams) {
    t13 = async () => await commonApiFetch({
      endpoint: "community-members/top",
      params: debouncedParams
    });
    $[34] = debouncedParams;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = {
      queryKey: t12,
      queryFn: t13,
      placeholderData: keepPreviousData
    };
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const {
    isLoading,
    isFetching,
    data: members
  } = useQuery(t14);
  let t15;
  if ($[39] !== createQueryString || $[40] !== pathname || $[41] !== router) {
    t15 = (updateItems_0, t16) => {
      const lowerCase_0 = t16 === undefined ? true : t16;
      const queryString = createQueryString(updateItems_0, lowerCase_0);
      const path = queryString ? `${pathname}?${queryString}` : pathname;
      if (path) {
        router.replace(path);
      }
    };
    $[39] = createQueryString;
    $[40] = pathname;
    $[41] = router;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const updateFields = t15;
  let t16;
  if ($[43] !== debouncedParams.sort || $[44] !== debouncedParams.sort_direction || $[45] !== updateFields) {
    t16 = async sortBy_0 => {
      const items = [{
        name: "sortBy",
        value: sortBy_0
      }, {
        name: "sortDirection",
        value: calculateSortDirection({
          newSortBy: sortBy_0,
          currentSortBy: debouncedParams.sort,
          currentSortDirection: debouncedParams.sort_direction
        })
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items);
    };
    $[43] = debouncedParams.sort;
    $[44] = debouncedParams.sort_direction;
    $[45] = updateFields;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const setSortBy = t16;
  let t17;
  let t18;
  if ($[47] !== activeGroupId || $[48] !== params.group_id || $[49] !== updateFields) {
    t17 = () => {
      const urlGroup = params.group_id ?? null;
      const stateGroup = activeGroupId ?? null;
      if (urlGroup === stateGroup) {
        return;
      }
      const items_0 = [{
        name: "group",
        value: stateGroup
      }, {
        name: "page",
        value: "1"
      }];
      updateFields(items_0, false);
    };
    t18 = [activeGroupId, params.group_id, updateFields];
    $[47] = activeGroupId;
    $[48] = params.group_id;
    $[49] = updateFields;
    $[50] = t17;
    $[51] = t18;
  } else {
    t17 = $[50];
    t18 = $[51];
  }
  useEffect(t17, t18);
  let t19;
  if ($[52] !== updateFields) {
    t19 = async page_0 => {
      const items_1 = [{
        name: "page",
        value: page_0.toString()
      }];
      await updateFields(items_1);
    };
    $[52] = updateFields;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const setPage = t19;
  const [totalPages, setTotalPages] = useState(1);
  let t20;
  if ($[54] !== debouncedParams.page || $[55] !== debouncedParams.page_size || $[56] !== isLoading || $[57] !== members || $[58] !== setPage) {
    t20 = () => {
      if (isLoading) {
        return;
      }
      if (!members?.count) {
        setPage(1);
        setTotalPages(1);
        return;
      }
      const pagesCount = Math.ceil(members.count / debouncedParams.page_size);
      if (pagesCount < debouncedParams.page) {
        setPage(pagesCount);
      }
      setTotalPages(pagesCount);
    };
    $[54] = debouncedParams.page;
    $[55] = debouncedParams.page_size;
    $[56] = isLoading;
    $[57] = members;
    $[58] = setPage;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  const t21 = members?.count;
  let t22;
  if ($[60] !== debouncedParams.page || $[61] !== debouncedParams.page_size || $[62] !== isLoading || $[63] !== setPage || $[64] !== t21) {
    t22 = [debouncedParams.page, debouncedParams.page_size, isLoading, t21, setPage];
    $[60] = debouncedParams.page;
    $[61] = debouncedParams.page_size;
    $[62] = isLoading;
    $[63] = setPage;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  useEffect(t20, t22);
  let t23;
  if ($[66] !== router) {
    t23 = () => router.push("/network/nerd");
    $[66] = router;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  const goToNerd = t23;
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  let t24;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setMobileFilterOpen(false);
    };
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== activeGroupId) {
    t25 = [activeGroupId];
    $[69] = activeGroupId;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  useEffect(t24, t25);
  let t26;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <h1 className="tw-mb-0 tw-text-xl tw-font-semibold tw-text-iron-50">Network</h1>;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const t27 = `tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-transition tw-duration-300 tw-ease-out focus:tw-outline-none ${activeGroupId ? "tw-border-primary-500 tw-bg-primary-500/20 tw-text-primary-300" : "tw-border-iron-600 tw-bg-iron-800 tw-text-iron-300 hover:tw-bg-iron-700"}`;
  let t28;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setMobileFilterOpen(true);
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <FunnelIcon className="tw-h-5 tw-w-5" />;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t27) {
    t30 = <button type="button" className={t27} onClick={t28} aria-label="Open filter panel">{t29}</button>;
    $[74] = t27;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setMobileSortOpen(true);
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <button type="button" className="tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-800 tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 focus:tw-outline-none sm:tw-hidden" onClick={t31} aria-label="Open sort options"><FontAwesomeIcon icon={faArrowDownWideShort} className="tw-h-5 tw-w-5" /></button>;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== t30) {
    t33 = <div className="tw-flex tw-items-center tw-gap-x-3">{t26}{t30}{t32}</div>;
    $[78] = t30;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span>Nerd view</span>;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <svg className="-tw-mr-1.5 tw-h-5 tw-w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== goToNerd) {
    t36 = <div className="tw-ml-auto tw-inline-flex tw-items-center tw-space-x-3"><button type="button" className="tw-inline-flex tw-items-center tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-200 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 focus:tw-z-10 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400" onClick={goToNerd}>{t34}{t35}</button></div>;
    $[82] = goToNerd;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  let t37;
  if ($[84] !== t33 || $[85] !== t36) {
    t37 = <div className="tw-flex tw-items-center tw-justify-between">{t33}{t36}</div>;
    $[84] = t33;
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== isFetching || $[88] !== isLoading || $[89] !== members || $[90] !== params.page_size || $[91] !== params.sort || $[92] !== params.sort_direction || $[93] !== setPage || $[94] !== setSortBy || $[95] !== totalPages) {
    t38 = <div className="tailwind-scope tw-mt-2 tw-flow-root lg:tw-mt-3">{members ? <><div className="tw-rounded-lg tw-bg-iron-950 tw-shadow sm:tw-divide-y sm:tw-divide-solid sm:tw-divide-iron-800 sm:tw-overflow-auto sm:tw-border sm:tw-border-solid sm:tw-border-iron-700"><CommunityMembersTable members={members.data} activeSort={params.sort} sortDirection={params.sort_direction} page={members.page} pageSize={params.page_size} isLoading={isFetching} onSort={setSortBy} /></div>{totalPages > 1 && <CommonTablePagination currentPage={members.page} setCurrentPage={setPage} totalPages={totalPages} haveNextPage={members.next} small={false} loading={isLoading} />}</> : <CommunityMembersTableSkeleton />}</div>;
    $[87] = isFetching;
    $[88] = isLoading;
    $[89] = members;
    $[90] = params.page_size;
    $[91] = params.sort;
    $[92] = params.sort_direction;
    $[93] = setPage;
    $[94] = setSortBy;
    $[95] = totalPages;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = () => setMobileFilterOpen(false);
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="tw-px-4"><GroupsSidebar /></div>;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== mobileFilterOpen) {
    t41 = <MobileWrapperDialog title="Filter" isOpen={mobileFilterOpen} onClose={t39} tall={true} fixedHeight={true}>{t40}</MobileWrapperDialog>;
    $[99] = mobileFilterOpen;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setMobileSortOpen(false);
    $[101] = t42;
  } else {
    t42 = $[101];
  }
  let t43;
  if ($[102] !== setSortBy) {
    t43 = sort_0 => {
      setSortBy(sort_0);
    };
    $[102] = setSortBy;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] !== params.sort || $[105] !== params.sort_direction || $[106] !== t43) {
    t44 = <CommunityMembersMobileSortContent activeSort={params.sort} sortDirection={params.sort_direction} onSort={t43} />;
    $[104] = params.sort;
    $[105] = params.sort_direction;
    $[106] = t43;
    $[107] = t44;
  } else {
    t44 = $[107];
  }
  let t45;
  if ($[108] !== mobileSortOpen || $[109] !== t44) {
    t45 = <MobileWrapperDialog title="Sort" isOpen={mobileSortOpen} onClose={t42}>{t44}</MobileWrapperDialog>;
    $[108] = mobileSortOpen;
    $[109] = t44;
    $[110] = t45;
  } else {
    t45 = $[110];
  }
  let t46;
  if ($[111] !== t37 || $[112] !== t38 || $[113] !== t41 || $[114] !== t45) {
    t46 = <div>{t37}{t38}{t41}{t45}</div>;
    $[111] = t37;
    $[112] = t38;
    $[113] = t41;
    $[114] = t45;
    $[115] = t46;
  } else {
    t46 = $[115];
  }
  return t46;
}